/**
 * Time zones, listed by the places people actually name.
 *
 * The IANA database is organised around history, not geography — which is why
 * Indiana alone has eight zones and none of them is called "Indiana". A teacher
 * in Elkhart should be able to type "Elkhart" and get the right answer without
 * knowing that her county was folded into America/Indiana/Indianapolis in 2006,
 * so every zone carries the towns it covers and the search reads those too.
 */
export const TIME_ZONES = [
    /* ------------------------------------------------------------- Indiana
     *
     * Listed first and in full. Most of the state is Eastern, the north-west
     * corner around Gary runs on Chicago time, and a handful of south-western
     * counties are Central — a genuine source of missed meetings.
     */
    {
        zone: 'America/Indiana/Indianapolis',
        label: 'Indianapolis — Eastern',
        region: 'Indiana',
        cities: [
            'Indianapolis',
            'Fort Wayne',
            'Elkhart',
            'Goshen',
            'South Bend',
            'Mishawaka',
            'Bloomington',
            'Carmel',
            'Fishers',
            'Noblesville',
            'Muncie',
            'Anderson',
            'Kokomo',
            'Lafayette',
            'West Lafayette',
            'Columbus',
            'Richmond',
            'Marion',
            'Terre Haute',
            'Greenwood',
            'Warsaw',
            'Nappanee',
            'Bristol',
            'Middlebury',
            'Wakarusa',
            'Granger',
            'Plymouth',
            'Angola',
            'Auburn',
            'New Castle',
        ],
    },
    {
        zone: 'America/Chicago',
        label: 'Gary and the north-west — Central',
        region: 'Indiana',
        cities: [
            'Gary',
            'Hammond',
            'Merrillville',
            'Valparaiso',
            'Michigan City',
            'Crown Point',
            'Portage',
            'East Chicago',
            'Lake County',
            'Porter County',
            'LaPorte',
        ],
    },
    {
        zone: 'America/Indiana/Knox',
        label: 'Knox and Starke County — Central',
        region: 'Indiana',
        cities: ['Knox', 'Starke County', 'North Judson', 'Hamlet'],
    },
    {
        zone: 'America/Indiana/Vincennes',
        label: 'Vincennes — Eastern',
        region: 'Indiana',
        cities: [
            'Vincennes',
            'Jasper',
            'Washington',
            'Princeton',
            'Huntingburg',
            'Daviess County',
            'Knox County',
            'Martin County',
            'Pike County',
            'Dubois County',
        ],
    },
    {
        zone: 'America/Indiana/Winamac',
        label: 'Winamac and Pulaski County — Eastern',
        region: 'Indiana',
        cities: ['Winamac', 'Pulaski County', 'Francesville', 'Medaryville'],
    },
    {
        zone: 'America/Indiana/Marengo',
        label: 'Marengo and Crawford County — Eastern',
        region: 'Indiana',
        cities: ['Marengo', 'Crawford County', 'English', 'Milltown'],
    },
    {
        zone: 'America/Indiana/Petersburg',
        label: 'Petersburg and Pike County — Eastern',
        region: 'Indiana',
        cities: ['Petersburg', 'Pike County', 'Winslow', 'Spurgeon'],
    },
    {
        zone: 'America/Indiana/Vevay',
        label: 'Vevay and Switzerland County — Eastern',
        region: 'Indiana',
        cities: ['Vevay', 'Switzerland County', 'Patriot'],
    },
    {
        zone: 'America/Indiana/Tell_City',
        label: 'Tell City and Perry County — Central',
        region: 'Indiana',
        cities: ['Tell City', 'Perry County', 'Cannelton', 'Troy'],
    },
    /* ------------------------------------------------------ United States */
    {
        zone: 'America/New_York',
        label: 'Eastern time',
        region: 'United States',
        cities: [
            'New York',
            'Detroit',
            'Grand Rapids',
            'Kalamazoo',
            'Lansing',
            'Ann Arbor',
            'Columbus',
            'Cleveland',
            'Cincinnati',
            'Toledo',
            'Dayton',
            'Akron',
            'Pittsburgh',
            'Philadelphia',
            'Harrisburg',
            'Erie',
            'Buffalo',
            'Rochester',
            'Syracuse',
            'Albany',
            'Boston',
            'Worcester',
            'Providence',
            'Hartford',
            'Bridgeport',
            'Portland ME',
            'Bangor',
            'Burlington VT',
            'Manchester NH',
            'Baltimore',
            'Washington DC',
            'Richmond',
            'Norfolk',
            'Roanoke',
            'Charlotte',
            'Raleigh',
            'Greensboro',
            'Durham',
            'Wilmington',
            'Charleston SC',
            'Columbia SC',
            'Greenville',
            'Atlanta',
            'Savannah',
            'Augusta',
            'Macon',
            'Jacksonville',
            'Orlando',
            'Tampa',
            'Miami',
            'Tallahassee',
            'Lexington',
            'Louisville',
            'Charleston WV',
            'Morgantown',
        ],
    },
    {
        zone: 'America/Chicago',
        label: 'Central time',
        region: 'United States',
        cities: [
            'Chicago',
            'Naperville',
            'Rockford',
            'Peoria',
            'Springfield IL',
            'Champaign',
            'Milwaukee',
            'Madison',
            'Green Bay',
            'Appleton',
            'Minneapolis',
            'Saint Paul',
            'Duluth',
            'Rochester MN',
            'Des Moines',
            'Cedar Rapids',
            'Davenport',
            'Iowa City',
            'Kansas City',
            'Saint Louis',
            'Springfield MO',
            'Columbia MO',
            'Omaha',
            'Lincoln',
            'Wichita',
            'Topeka',
            'Little Rock',
            'Fayetteville AR',
            'Memphis',
            'Nashville',
            'Knoxville',
            'Chattanooga',
            'Birmingham',
            'Montgomery',
            'Mobile',
            'Huntsville',
            'Jackson MS',
            'New Orleans',
            'Baton Rouge',
            'Shreveport',
            'Houston',
            'Dallas',
            'Fort Worth',
            'Austin',
            'San Antonio',
            'Corpus Christi',
            'Waco',
            'Lubbock',
            'Amarillo',
            'Oklahoma City',
            'Tulsa',
            'Fargo',
            'Bismarck',
            'Sioux Falls',
            'Rapid City',
        ],
    },
    {
        zone: 'America/Denver',
        label: 'Mountain time',
        region: 'United States',
        cities: [
            'Denver',
            'Colorado Springs',
            'Boulder',
            'Fort Collins',
            'Aurora',
            'Pueblo',
            'Salt Lake City',
            'Provo',
            'Ogden',
            'Albuquerque',
            'Santa Fe',
            'Las Cruces',
            'Cheyenne',
            'Casper',
            'Billings',
            'Missoula',
            'Bozeman',
            'Great Falls',
            'Boise',
            'Idaho Falls',
            'El Paso',
        ],
    },
    {
        zone: 'America/Phoenix',
        label: 'Arizona — no daylight saving',
        region: 'United States',
        cities: [
            'Phoenix',
            'Tucson',
            'Mesa',
            'Scottsdale',
            'Chandler',
            'Glendale AZ',
            'Flagstaff',
            'Yuma',
        ],
    },
    {
        zone: 'America/Los_Angeles',
        label: 'Pacific time',
        region: 'United States',
        cities: [
            'Los Angeles',
            'San Diego',
            'San Francisco',
            'San Jose',
            'Sacramento',
            'Fresno',
            'Oakland',
            'Long Beach',
            'Bakersfield',
            'Riverside',
            'Stockton',
            'Seattle',
            'Spokane',
            'Tacoma',
            'Vancouver WA',
            'Bellevue',
            'Portland',
            'Eugene',
            'Salem',
            'Bend',
            'Las Vegas',
            'Reno',
            'Henderson',
            'Carson City',
        ],
    },
    {
        zone: 'America/Anchorage',
        label: 'Alaska time',
        region: 'United States',
        cities: ['Anchorage', 'Fairbanks', 'Juneau', 'Wasilla', 'Sitka', 'Ketchikan'],
    },
    {
        zone: 'Pacific/Honolulu',
        label: 'Hawaii — no daylight saving',
        region: 'United States',
        cities: ['Honolulu', 'Hilo', 'Kailua', 'Maui', 'Kauai'],
    },
    {
        zone: 'America/Adak',
        label: 'Aleutian Islands',
        region: 'United States',
        cities: ['Adak', 'Atka'],
    },
    {
        zone: 'America/Puerto_Rico',
        label: 'Puerto Rico and the Virgin Islands',
        region: 'United States',
        cities: ['San Juan', 'Ponce', 'Bayamón', 'Charlotte Amalie', 'St Croix'],
    },
    {
        zone: 'Pacific/Guam',
        label: 'Guam and the Northern Marianas',
        region: 'United States',
        cities: ['Hagåtña', 'Dededo', 'Saipan'],
    },
    {
        zone: 'Pacific/Pago_Pago',
        label: 'American Samoa',
        region: 'United States',
        cities: ['Pago Pago'],
    },
    /* ------------------------------------ States split across two zones */
    {
        zone: 'America/Detroit',
        label: 'Michigan — Detroit and the Lower Peninsula',
        region: 'Split states',
        cities: ['Detroit', 'Flint', 'Saginaw', 'Traverse City', 'Marquette'],
    },
    {
        zone: 'America/Menominee',
        label: 'Michigan — western Upper Peninsula',
        region: 'Split states',
        cities: ['Menominee', 'Iron Mountain', 'Ironwood'],
    },
    {
        zone: 'America/Kentucky/Louisville',
        label: 'Kentucky — Louisville and the east',
        region: 'Split states',
        cities: ['Louisville', 'Lexington', 'Frankfort', 'Covington', 'Bowling Green'],
    },
    {
        zone: 'America/Kentucky/Monticello',
        label: 'Kentucky — Wayne County',
        region: 'Split states',
        cities: ['Monticello', 'Wayne County'],
    },
    {
        zone: 'America/North_Dakota/Center',
        label: 'North Dakota — Oliver County',
        region: 'Split states',
        cities: ['Center', 'Oliver County'],
    },
    {
        zone: 'America/North_Dakota/New_Salem',
        label: 'North Dakota — Morton County',
        region: 'Split states',
        cities: ['New Salem', 'Morton County'],
    },
    {
        zone: 'America/North_Dakota/Beulah',
        label: 'North Dakota — Mercer County',
        region: 'Split states',
        cities: ['Beulah', 'Mercer County'],
    },
    {
        zone: 'America/Boise',
        label: 'Idaho — southern Idaho and eastern Oregon',
        region: 'Split states',
        cities: ['Boise', 'Twin Falls', 'Pocatello', 'Ontario OR'],
    },
    {
        zone: 'America/Juneau',
        label: 'Alaska — Juneau and the panhandle',
        region: 'Split states',
        cities: ['Juneau', 'Haines', 'Skagway'],
    },
    {
        zone: 'America/Nome',
        label: 'Alaska — Nome and the west coast',
        region: 'Split states',
        cities: ['Nome', 'Kotzebue', 'Unalakleet'],
    },
    /* ----------------------------------------------------------- Canada */
    {
        zone: 'America/Toronto',
        label: 'Toronto and Ottawa',
        region: 'Canada',
        cities: ['Toronto', 'Ottawa', 'Hamilton', 'London ON', 'Windsor', 'Kingston', 'Sudbury'],
    },
    {
        zone: 'America/Montreal',
        label: 'Montréal and Québec',
        region: 'Canada',
        cities: ['Montréal', 'Québec City', 'Sherbrooke', 'Gatineau'],
    },
    {
        zone: 'America/Winnipeg',
        label: 'Winnipeg',
        region: 'Canada',
        cities: ['Winnipeg', 'Brandon', 'Thunder Bay'],
    },
    {
        zone: 'America/Regina',
        label: 'Saskatchewan — no daylight saving',
        region: 'Canada',
        cities: ['Regina', 'Saskatoon', 'Moose Jaw'],
    },
    {
        zone: 'America/Edmonton',
        label: 'Edmonton and Calgary',
        region: 'Canada',
        cities: ['Edmonton', 'Calgary', 'Red Deer', 'Lethbridge', 'Banff'],
    },
    {
        zone: 'America/Vancouver',
        label: 'Vancouver and Victoria',
        region: 'Canada',
        cities: ['Vancouver', 'Victoria', 'Kelowna', 'Surrey', 'Whistler'],
    },
    {
        zone: 'America/Halifax',
        label: 'Halifax and the Maritimes',
        region: 'Canada',
        cities: ['Halifax', 'Moncton', 'Fredericton', 'Charlottetown', 'Sydney NS'],
    },
    {
        zone: 'America/St_Johns',
        label: "St John's, Newfoundland",
        region: 'Canada',
        cities: ["St John's", 'Corner Brook', 'Gander'],
    },
    {
        zone: 'America/Whitehorse',
        label: 'Yukon',
        region: 'Canada',
        cities: ['Whitehorse', 'Dawson City'],
    },
    {
        zone: 'America/Yellowknife',
        label: 'Northwest Territories',
        region: 'Canada',
        cities: ['Yellowknife', 'Inuvik', 'Hay River'],
    },
    {
        zone: 'America/Iqaluit',
        label: 'Nunavut',
        region: 'Canada',
        cities: ['Iqaluit', 'Rankin Inlet'],
    },
    /* ------------------------------------------- Mexico and the Caribbean */
    {
        zone: 'America/Mexico_City',
        label: 'Mexico City',
        region: 'Mexico and the Caribbean',
        cities: ['Mexico City', 'Guadalajara', 'Puebla', 'Monterrey', 'León'],
    },
    {
        zone: 'America/Tijuana',
        label: 'Tijuana',
        region: 'Mexico and the Caribbean',
        cities: ['Tijuana', 'Mexicali', 'Ensenada'],
    },
    {
        zone: 'America/Cancun',
        label: 'Cancún',
        region: 'Mexico and the Caribbean',
        cities: ['Cancún', 'Playa del Carmen', 'Cozumel', 'Chetumal'],
    },
    {
        zone: 'America/Havana',
        label: 'Havana',
        region: 'Mexico and the Caribbean',
        cities: ['Havana', 'Santiago de Cuba'],
    },
    {
        zone: 'America/Santo_Domingo',
        label: 'Santo Domingo',
        region: 'Mexico and the Caribbean',
        cities: ['Santo Domingo', 'Santiago DR'],
    },
    {
        zone: 'America/Port-au-Prince',
        label: 'Port-au-Prince',
        region: 'Mexico and the Caribbean',
        cities: ['Port-au-Prince'],
    },
    {
        zone: 'America/Jamaica',
        label: 'Kingston',
        region: 'Mexico and the Caribbean',
        cities: ['Kingston', 'Montego Bay'],
    },
    {
        zone: 'America/Nassau',
        label: 'Nassau',
        region: 'Mexico and the Caribbean',
        cities: ['Nassau', 'Freeport'],
    },
    /* -------------------------------------------- Central and South America */
    {
        zone: 'America/Guatemala',
        label: 'Guatemala City',
        region: 'Central and South America',
        cities: ['Guatemala City', 'Quetzaltenango'],
    },
    {
        zone: 'America/Costa_Rica',
        label: 'San José',
        region: 'Central and South America',
        cities: ['San José', 'Alajuela'],
    },
    {
        zone: 'America/Panama',
        label: 'Panama City',
        region: 'Central and South America',
        cities: ['Panama City', 'Colón'],
    },
    {
        zone: 'America/Bogota',
        label: 'Bogotá',
        region: 'Central and South America',
        cities: ['Bogotá', 'Medellín', 'Cali', 'Cartagena'],
    },
    {
        zone: 'America/Lima',
        label: 'Lima',
        region: 'Central and South America',
        cities: ['Lima', 'Arequipa', 'Cusco'],
    },
    {
        zone: 'America/Caracas',
        label: 'Caracas',
        region: 'Central and South America',
        cities: ['Caracas', 'Maracaibo'],
    },
    {
        zone: 'America/Santiago',
        label: 'Santiago',
        region: 'Central and South America',
        cities: ['Santiago', 'Valparaíso'],
    },
    {
        zone: 'America/Argentina/Buenos_Aires',
        label: 'Buenos Aires',
        region: 'Central and South America',
        cities: ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza'],
    },
    {
        zone: 'America/Sao_Paulo',
        label: 'São Paulo',
        region: 'Central and South America',
        cities: ['São Paulo', 'Rio de Janeiro', 'Brasília', 'Belo Horizonte'],
    },
    {
        zone: 'America/Montevideo',
        label: 'Montevideo',
        region: 'Central and South America',
        cities: ['Montevideo'],
    },
    {
        zone: 'America/La_Paz',
        label: 'La Paz',
        region: 'Central and South America',
        cities: ['La Paz', 'Santa Cruz'],
    },
    {
        zone: 'America/Asuncion',
        label: 'Asunción',
        region: 'Central and South America',
        cities: ['Asunción'],
    },
    {
        zone: 'America/Guayaquil',
        label: 'Quito',
        region: 'Central and South America',
        cities: ['Quito', 'Guayaquil'],
    },
    /* ----------------------------------------------------------- Europe */
    {
        zone: 'Europe/London',
        label: 'London',
        region: 'Europe',
        cities: [
            'London',
            'Manchester',
            'Birmingham',
            'Glasgow',
            'Edinburgh',
            'Cardiff',
            'Belfast',
            'Liverpool',
            'Leeds',
        ],
    },
    {
        zone: 'Europe/Dublin',
        label: 'Dublin',
        region: 'Europe',
        cities: ['Dublin', 'Cork', 'Galway'],
    },
    { zone: 'Europe/Lisbon', label: 'Lisbon', region: 'Europe', cities: ['Lisbon', 'Porto', 'Faro'] },
    {
        zone: 'Europe/Madrid',
        label: 'Madrid',
        region: 'Europe',
        cities: ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Bilbao'],
    },
    {
        zone: 'Europe/Paris',
        label: 'Paris',
        region: 'Europe',
        cities: ['Paris', 'Lyon', 'Marseille', 'Toulouse', 'Bordeaux', 'Nice'],
    },
    {
        zone: 'Europe/Brussels',
        label: 'Brussels',
        region: 'Europe',
        cities: ['Brussels', 'Antwerp', 'Ghent'],
    },
    {
        zone: 'Europe/Amsterdam',
        label: 'Amsterdam',
        region: 'Europe',
        cities: ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht'],
    },
    {
        zone: 'Europe/Berlin',
        label: 'Berlin',
        region: 'Europe',
        cities: ['Berlin', 'Munich', 'Hamburg', 'Cologne', 'Frankfurt', 'Stuttgart'],
    },
    {
        zone: 'Europe/Zurich',
        label: 'Zürich',
        region: 'Europe',
        cities: ['Zürich', 'Geneva', 'Bern', 'Basel'],
    },
    {
        zone: 'Europe/Vienna',
        label: 'Vienna',
        region: 'Europe',
        cities: ['Vienna', 'Salzburg', 'Graz', 'Innsbruck'],
    },
    {
        zone: 'Europe/Rome',
        label: 'Rome',
        region: 'Europe',
        cities: ['Rome', 'Milan', 'Naples', 'Turin', 'Florence', 'Venice'],
    },
    { zone: 'Europe/Prague', label: 'Prague', region: 'Europe', cities: ['Prague', 'Brno'] },
    {
        zone: 'Europe/Warsaw',
        label: 'Warsaw',
        region: 'Europe',
        cities: ['Warsaw', 'Kraków', 'Gdańsk', 'Wrocław'],
    },
    {
        zone: 'Europe/Budapest',
        label: 'Budapest',
        region: 'Europe',
        cities: ['Budapest', 'Debrecen'],
    },
    {
        zone: 'Europe/Stockholm',
        label: 'Stockholm',
        region: 'Europe',
        cities: ['Stockholm', 'Gothenburg', 'Malmö'],
    },
    { zone: 'Europe/Oslo', label: 'Oslo', region: 'Europe', cities: ['Oslo', 'Bergen', 'Trondheim'] },
    {
        zone: 'Europe/Copenhagen',
        label: 'Copenhagen',
        region: 'Europe',
        cities: ['Copenhagen', 'Aarhus'],
    },
    {
        zone: 'Europe/Helsinki',
        label: 'Helsinki',
        region: 'Europe',
        cities: ['Helsinki', 'Tampere', 'Turku'],
    },
    { zone: 'Europe/Athens', label: 'Athens', region: 'Europe', cities: ['Athens', 'Thessaloniki'] },
    {
        zone: 'Europe/Bucharest',
        label: 'Bucharest',
        region: 'Europe',
        cities: ['Bucharest', 'Cluj-Napoca'],
    },
    {
        zone: 'Europe/Kyiv',
        label: 'Kyiv',
        region: 'Europe',
        cities: ['Kyiv', 'Lviv', 'Odesa', 'Kharkiv'],
    },
    {
        zone: 'Europe/Istanbul',
        label: 'Istanbul',
        region: 'Europe',
        cities: ['Istanbul', 'Ankara', 'Izmir'],
    },
    {
        zone: 'Europe/Moscow',
        label: 'Moscow',
        region: 'Europe',
        cities: ['Moscow', 'Saint Petersburg', 'Kazan'],
    },
    { zone: 'Atlantic/Reykjavik', label: 'Reykjavík', region: 'Europe', cities: ['Reykjavík'] },
    /* --------------------------------------------- Africa and the Middle East */
    {
        zone: 'Africa/Casablanca',
        label: 'Casablanca',
        region: 'Africa and the Middle East',
        cities: ['Casablanca', 'Rabat', 'Marrakesh'],
    },
    {
        zone: 'Africa/Lagos',
        label: 'Lagos',
        region: 'Africa and the Middle East',
        cities: ['Lagos', 'Abuja', 'Kano', 'Accra'],
    },
    {
        zone: 'Africa/Cairo',
        label: 'Cairo',
        region: 'Africa and the Middle East',
        cities: ['Cairo', 'Alexandria', 'Giza'],
    },
    {
        zone: 'Africa/Nairobi',
        label: 'Nairobi',
        region: 'Africa and the Middle East',
        cities: ['Nairobi', 'Mombasa', 'Kampala', 'Addis Ababa', 'Dar es Salaam'],
    },
    {
        zone: 'Africa/Johannesburg',
        label: 'Johannesburg',
        region: 'Africa and the Middle East',
        cities: ['Johannesburg', 'Cape Town', 'Durban', 'Pretoria'],
    },
    {
        zone: 'Asia/Jerusalem',
        label: 'Jerusalem',
        region: 'Africa and the Middle East',
        cities: ['Jerusalem', 'Tel Aviv', 'Haifa'],
    },
    {
        zone: 'Asia/Dubai',
        label: 'Dubai',
        region: 'Africa and the Middle East',
        cities: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Muscat'],
    },
    {
        zone: 'Asia/Riyadh',
        label: 'Riyadh',
        region: 'Africa and the Middle East',
        cities: ['Riyadh', 'Jeddah', 'Mecca', 'Doha', 'Kuwait City', 'Manama'],
    },
    {
        zone: 'Asia/Tehran',
        label: 'Tehran',
        region: 'Africa and the Middle East',
        cities: ['Tehran', 'Mashhad', 'Isfahan'],
    },
    /* ------------------------------------------------------------- Asia */
    {
        zone: 'Asia/Karachi',
        label: 'Karachi',
        region: 'Asia',
        cities: ['Karachi', 'Lahore', 'Islamabad'],
    },
    {
        zone: 'Asia/Kolkata',
        label: 'India',
        region: 'Asia',
        cities: ['Mumbai', 'Delhi', 'Bengaluru', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Colombo'],
    },
    { zone: 'Asia/Kathmandu', label: 'Kathmandu', region: 'Asia', cities: ['Kathmandu', 'Pokhara'] },
    { zone: 'Asia/Dhaka', label: 'Dhaka', region: 'Asia', cities: ['Dhaka', 'Chittagong'] },
    {
        zone: 'Asia/Bangkok',
        label: 'Bangkok',
        region: 'Asia',
        cities: ['Bangkok', 'Chiang Mai', 'Hanoi', 'Ho Chi Minh City', 'Phnom Penh', 'Vientiane'],
    },
    {
        zone: 'Asia/Jakarta',
        label: 'Jakarta',
        region: 'Asia',
        cities: ['Jakarta', 'Surabaya', 'Bandung'],
    },
    {
        zone: 'Asia/Singapore',
        label: 'Singapore',
        region: 'Asia',
        cities: ['Singapore', 'Kuala Lumpur', 'Johor Bahru'],
    },
    {
        zone: 'Asia/Manila',
        label: 'Manila',
        region: 'Asia',
        cities: ['Manila', 'Cebu', 'Davao', 'Quezon City'],
    },
    { zone: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Asia', cities: ['Hong Kong', 'Macau'] },
    {
        zone: 'Asia/Shanghai',
        label: 'Shanghai',
        region: 'Asia',
        cities: ['Shanghai', 'Beijing', 'Shenzhen', 'Guangzhou', 'Chengdu', 'Xi’an'],
    },
    {
        zone: 'Asia/Taipei',
        label: 'Taipei',
        region: 'Asia',
        cities: ['Taipei', 'Kaohsiung', 'Taichung'],
    },
    { zone: 'Asia/Seoul', label: 'Seoul', region: 'Asia', cities: ['Seoul', 'Busan', 'Incheon'] },
    {
        zone: 'Asia/Tokyo',
        label: 'Tokyo',
        region: 'Asia',
        cities: ['Tokyo', 'Osaka', 'Kyoto', 'Yokohama', 'Sapporo', 'Okinawa'],
    },
    /* ------------------------------------------------- Australia and Oceania */
    {
        zone: 'Australia/Perth',
        label: 'Perth',
        region: 'Australia and Oceania',
        cities: ['Perth', 'Fremantle'],
    },
    {
        zone: 'Australia/Darwin',
        label: 'Darwin',
        region: 'Australia and Oceania',
        cities: ['Darwin', 'Alice Springs'],
    },
    {
        zone: 'Australia/Adelaide',
        label: 'Adelaide',
        region: 'Australia and Oceania',
        cities: ['Adelaide'],
    },
    {
        zone: 'Australia/Brisbane',
        label: 'Brisbane',
        region: 'Australia and Oceania',
        cities: ['Brisbane', 'Gold Coast', 'Cairns', 'Townsville'],
    },
    {
        zone: 'Australia/Sydney',
        label: 'Sydney',
        region: 'Australia and Oceania',
        cities: ['Sydney', 'Canberra', 'Newcastle', 'Wollongong'],
    },
    {
        zone: 'Australia/Melbourne',
        label: 'Melbourne',
        region: 'Australia and Oceania',
        cities: ['Melbourne', 'Geelong', 'Ballarat'],
    },
    {
        zone: 'Australia/Hobart',
        label: 'Hobart',
        region: 'Australia and Oceania',
        cities: ['Hobart', 'Launceston'],
    },
    {
        zone: 'Pacific/Auckland',
        label: 'Auckland',
        region: 'Australia and Oceania',
        cities: ['Auckland', 'Wellington', 'Christchurch', 'Queenstown'],
    },
    {
        zone: 'Pacific/Fiji',
        label: 'Fiji',
        region: 'Australia and Oceania',
        cities: ['Suva', 'Nadi'],
    },
    {
        zone: 'Pacific/Port_Moresby',
        label: 'Port Moresby',
        region: 'Australia and Oceania',
        cities: ['Port Moresby'],
    },
];
/** The headings, in the order they should be shown. */
export const TIME_ZONE_REGIONS = [...new Set(TIME_ZONES.map((z) => z.region))];
/**
 * Matches on the label, the region, the IANA name and every town listed —
 * so "Elkhart", "Indiana" and "America/Indiana" all find the same entry.
 */
export function searchTimeZones(query) {
    const q = query.trim().toLowerCase();
    if (!q)
        return TIME_ZONES;
    return TIME_ZONES.filter((z) => z.label.toLowerCase().includes(q) ||
        z.region.toLowerCase().includes(q) ||
        z.zone.toLowerCase().replace(/_/g, ' ').includes(q) ||
        z.cities.some((c) => c.toLowerCase().includes(q)));
}
/** The zone the browser thinks it is in — the sensible default. */
export function deviceTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
/**
 * "Tuesday, 4:32 PM" in the given zone — used to show what a choice means
 * before committing to it, and to check the clock is right afterwards.
 */
export function zoneClock(zone, now = new Date()) {
    try {
        return new Intl.DateTimeFormat(undefined, {
            timeZone: zone,
            weekday: 'short',
            hour: 'numeric',
            minute: '2-digit',
        }).format(now);
    }
    catch {
        // An unknown zone shouldn't blank the whole list.
        return '—';
    }
}
//# sourceMappingURL=timezones.js.map